import { disclosedText, fromFlag, tasks, WITHHELD_BECAUSE } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure derivations for the Tasks surface. No React, no fetching — everything
 * here is a decision about WORDS, which is where this domain can be quietly
 * betrayed.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE THIS FILE EXISTS TO KEEP
 * ---------------------------------------------------------------------------
 * A person marking work done is a CLAIM BY THAT PERSON. Phibian observing the
 * underlying condition resolve is a VERIFICATION. The API cannot confuse them —
 * `verified` is unreachable for a `human_attested` task by database constraint,
 * and no HTTP route offers verification at all — but a screen can, in one line,
 * by rendering a checkmark over `completion_claimed` or by putting the word
 * "verified" in a sentence that describes an attestation.
 *
 * So the affirmation "Phibian verified this." is produced in exactly ONE place
 * ({@link completionPresentation}), it is produced only from
 * `tasks.completionClaim(...).phibianVerified`, and that flag is computed from
 * the verification stamp rather than from the status word. Everything else this
 * file returns for a claimed task NAMES THE PERSON and says, in the product's
 * own voice, that nothing has been verified.
 */

export type CompletionKind = tasks.CompletionClaimKind;

/**
 * How a task's completion should read on screen.
 *
 * `affirmation` is the load-bearing field: it is non-null ONLY for a genuine
 * Phibian verification, and it is the only string in the product that says so.
 * `claimant` is non-null whenever a person is being quoted, so a surface can
 * never render an attestation without the name of whoever made it.
 */
export interface CompletionPresentation {
  kind: CompletionKind;
  /** For `data-completion` on the rendered element — testable, not decorative. */
  token: CompletionKind;
  /** Short label for a pill. Never says "verified" unless it IS verified. */
  label: string;
  variant: Severity;
  /**
   * "Phibian verified this." — and NOTHING else ever returns it. Null for
   * every claim, in every mode, including a hybrid claim Phibian is still
   * watching.
   */
  affirmation: string | null;
  /** The `app_user.id` of the person whose claim this is, when there is one. */
  claimantUserId: string | null;
  /** What the surface says about the claim. Always attributes it to a person. */
  claimSentence: string | null;
  /** Why this is not (or is) verified, in plain words. */
  assuranceSentence: string;
}

const NOT_OBSERVABLE =
  'Phibian cannot observe this outcome, so it can never be verified — it rests on this account.';
const STILL_WATCHING =
  'Phibian is still watching the underlying condition and has not seen it resolve. Not verified yet.';

/**
 * The single decision point. `who` resolves an `app_user.id` to a display name;
 * it is passed in rather than looked up here so this stays pure and so the
 * caller owns the three-way "why is this name missing" distinction.
 */
export function completionPresentation(
  task: tasks.TaskView,
  who: (userId: string | null) => string,
): CompletionPresentation {
  const claim = tasks.completionClaim(task);

  if (claim.phibianVerified) {
    return {
      kind: 'verified',
      token: 'verified',
      label: 'Phibian verified',
      variant: 'good',
      // The one place in the product that says this.
      affirmation: 'Phibian verified this.',
      claimantUserId: claim.attestedByUserId,
      claimSentence: claim.attestedByUserId
        ? `${who(claim.attestedByUserId)} marked this done first.`
        : null,
      assuranceSentence: `Phibian observed the underlying condition resolve: ${task.underlyingCondition}`,
    };
  }

  if (claim.kind === 'watching') {
    const name = who(claim.attestedByUserId);
    return {
      kind: 'watching',
      token: 'watching',
      // "Claimed", never "complete" — the obligation is not discharged.
      label: 'Claimed · Phibian watching',
      variant: 'look',
      affirmation: null,
      claimantUserId: claim.attestedByUserId,
      claimSentence: `${name} marked this done. This is ${name}’s account of what happened.`,
      assuranceSentence: STILL_WATCHING,
    };
  }

  if (claim.kind === 'claimed') {
    const name = who(claim.attestedByUserId);
    return {
      kind: 'claimed',
      token: 'claimed',
      label: 'Claimed by a person',
      variant: 'look',
      affirmation: null,
      claimantUserId: claim.attestedByUserId,
      claimSentence: `${name} marked this done. This is ${name}’s account of what happened.`,
      assuranceSentence: NOT_OBSERVABLE,
    };
  }

  return {
    kind: 'none',
    token: 'none',
    label: 'Outstanding',
    variant: 'plain',
    affirmation: null,
    claimantUserId: null,
    claimSentence: null,
    assuranceSentence: tasks.VERIFICATION_MODE_EXPLANATION[task.verificationMode],
  };
}

/**
 * The status word, for a reader.
 *
 * `completion_claimed` reads as "Marked done — not verified", NOT as "Complete"
 * and not as anything containing an unqualified assurance. That is the whole
 * defect this label exists to avoid: a status column that says "Complete" beside
 * a mode column that says "hybrid" leaves the reader to do the reconciling, and
 * they will do it in the generous direction.
 */
/*
 * TOTAL OVER `TaskStatus`, INCLUDING THE RESERVED ONE. `escalated` is not a
 * status this platform can produce (`RESERVED_UNPERFORMED_TASK_STATUSES` in
 * `@cwos/api-client`), and it is deliberately still labelled here: a label map
 * with a hole renders `undefined` in a status column the day a row turns up
 * carrying it — from an import, or from the engine when somebody builds one.
 * Labelling a status a reader might see is not the same act as OFFERING it as a
 * filter value, and only the second is a claim about what the platform does.
 */
export const STATUS_LABEL: Readonly<Record<tasks.TaskStatus, string>> = Object.freeze({
  open: 'Open',
  assigned: 'Assigned',
  escalated: 'Escalated',
  completion_claimed: 'Marked done — not verified',
  verified: 'Verified by Phibian',
  reopened: 'Reopened',
  closed: 'Closed',
});

export const STATUS_VARIANT: Readonly<Record<tasks.TaskStatus, Severity>> = Object.freeze({
  open: 'plain',
  assigned: 'sky',
  escalated: 'urgent',
  completion_claimed: 'look',
  verified: 'good',
  reopened: 'look',
  closed: 'plain',
});

export const PRIORITY_VARIANT: Readonly<Record<tasks.TaskPriority, Severity>> = Object.freeze({
  low: 'plain',
  normal: 'plain',
  high: 'look',
  urgent: 'urgent',
});

export const PRIORITY_LABEL: Readonly<Record<tasks.TaskPriority, string>> = Object.freeze({
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
});

/**
 * WHO RAISED IT, IN WORDS.
 *
 * The keys were `agent_generated` and `human_created` — two strings that appear
 * NOWHERE in `apps/api/src`. The server has always sent `'phibian'` or
 * `'management'` (`task_origin_ck`, migration 1758000000000), so this map
 * missed on every real task and both `TaskDetail` and `TasksQueue` rendered
 * `undefined` into the origin pill. It type-checked the whole time, because the
 * type it was keyed by was itself invented.
 *
 * `tasks.TaskOrigin` is now `@cwos/shared`'s `TASK_ORIGINS`, which is also what
 * `TASK_MODEL.origins` is on the server, so `Record<tasks.TaskOrigin, string>`
 * is a real totality check against the server's vocabulary rather than against
 * a second one. `test/unit/task-origin-labels.test.ts` asserts the same thing
 * at runtime, since a type cannot fail a test.
 */
export const ORIGIN_LABEL: Readonly<Record<tasks.TaskOrigin, string>> = Object.freeze({
  phibian: 'Raised by Phibian',
  management: 'Raised by a person',
});

/**
 * When this is due, as a sentence — or an honest silence.
 *
 * A task with neither an instant nor a window has NO due date, and the correct
 * rendering of that is "No due date", not "—" and certainly not today. A window
 * is rendered as a window, because a task due "some time on Thursday" is a
 * different obligation from one due at 09:00 and the difference is the
 * customer's, not ours to flatten.
 */
export function dueSentence(task: tasks.TaskView, now: Date): string {
  if (task.dueAt) {
    const due = new Date(task.dueAt);
    if (Number.isNaN(due.getTime())) return 'Due date unreadable';
    return `${overdue(due, now) ? 'Overdue since' : 'Due'} ${formatInstant(due)}`;
  }
  if (task.dueWindowStart && task.dueWindowEnd) {
    const end = new Date(task.dueWindowEnd);
    const start = new Date(task.dueWindowStart);
    if (Number.isNaN(end.getTime()) || Number.isNaN(start.getTime())) {
      return 'Due window unreadable';
    }
    const prefix = overdue(end, now) ? 'Window closed' : 'Due between';
    return `${prefix} ${formatInstant(start)} and ${formatInstant(end)}`;
  }
  return 'No due date';
}

function overdue(deadline: Date, now: Date): boolean {
  return deadline.getTime() < now.getTime();
}

function formatInstant(when: Date): string {
  return when.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The escalation sentence — FOUR states, and none of them a promise the
 * platform cannot keep.
 *
 * A task with no escalation policy DOES NOT ESCALATE. Saying "escalates after
 * 24 hours" because that is a common default would be the product inventing an
 * operational promise the customer never made.
 *
 * WHAT CHANGED. This used to say, for a task WITH a policy, "This platform does
 * not escalate tasks, so it has never fired" — which was exactly true:
 * `POST /api/tasks/escalation-policies` recorded a policy, nothing read one
 * back, no scheduler consumed one, and `escalation_count` was 0 on every row in
 * every tenant. The escalation sweep
 * (`tasks/service.runTaskEscalations`, on `system.task-escalations`) now
 * performs it, so that sentence became the opposite defect — a capability the
 * product HAS, denied.
 *
 * THE STATES, KEPT APART:
 *
 *   ABSENT        no policy. This task does not escalate, and never will.
 *   UNESCALATABLE a policy IS attached and the task has NO due boundary, so
 *                 nothing can ever be overdue and the policy cannot fire. This
 *                 is the branch that would otherwise read as "armed": the sweep
 *                 requires `coalesce(due_at, due_window_end) is not null`, and
 *                 a customer who attached a policy to a task with no deadline
 *                 has configured something that will never happen.
 *   ARMED         a policy, a deadline, and no escalation yet. "Yet" is now a
 *                 true word: the sweep runs every five minutes.
 *   MEASURED      a count above zero — rendered as the count it is.
 *
 * AND THE HALF THAT IS STILL NOT PERFORMED IS SAID OUT LOUD. Escalating raises
 * the task's STATUS; it does not route the task to anybody and it notifies
 * nobody, because `escalate_to` and `notify_channel` are read by nothing (see
 * `RESERVED_UNPERFORMED_ESCALATION_ROUTING` on the server). A screen that said
 * "it will be escalated" full stop would be making the same shape of promise
 * this function was rewritten to remove, one field over.
 */
export function escalationSentence(task: tasks.TaskView): string {
  if (task.escalationCount > 0) {
    const times = task.escalationCount === 1 ? 'once' : `${task.escalationCount} times`;
    return `Escalated ${times}.`;
  }
  if (!task.escalationPolicyId) return 'No escalation policy — this task does not escalate.';
  // `!x` rather than `=== null`: the type says `string | null`, and a payload
  // that simply omits the field would otherwise take the ARMED branch and tell
  // somebody a policy is live on a task that can never be overdue.
  if (!task.dueAt && !task.dueWindowEnd) {
    return (
      'An escalation policy is recorded, and this task has no due date — nothing can be ' +
      'overdue, so the policy cannot fire.'
    );
  }
  return (
    'An escalation policy is recorded and has not fired. Escalating raises the task’s ' +
    'status; it notifies nobody.'
  );
}

/** The mode, as a short label a column can carry. */
export function modeLabel(mode: tasks.VerificationMode): string {
  return tasks.VERIFICATION_MODE_LABEL[mode];
}

/**
 * WHAT THIS TASK IS CALLED, given that the server may have withheld it.
 *
 * A task routed to a role queue whose underlying client this reader does not
 * independently reach arrives with `objectDetailWithheld: true` and its prose
 * nulled. `{task.title}` in JSX renders `null` as NOTHING — a heading that is
 * simply not there, which reads as a task with no name rather than as one whose
 * name is not this reader's to see. That is the affirmative false statement
 * `docs/withholding-contract.md` ranks worst.
 *
 * ONE implementation for the queue and the detail, so the two cannot word it
 * differently. The FLAG is consulted before the value; `fromFlag` requires the
 * absence label precisely so a caller has to notice that an absence and a
 * withholding are two different sentences.
 */
export function taskTitle(task: tasks.TaskView): string {
  return disclosedText(
    fromFlag(
      task.title,
      task.objectDetailWithheld,
      'Untitled task',
      WITHHELD_BECAUSE.taskObjectDetail,
    ),
    (v) => v,
  );
}

/**
 * The task's prose, as three distinct answers rather than two.
 *
 *   'shown'    — render the text.
 *   'withheld' — render `placeholder`, which says so.
 *   'absent'   — there is genuinely nothing to say; render nothing.
 *
 * `description` is legitimately empty on a complete task (it defaults to `''`),
 * which is exactly why the flag has to be asked first: `task.description ? ... :
 * null` cannot tell an unwritten description from a withheld one.
 */
export function taskProse(
  value: string | null,
  task: Pick<tasks.TaskView, 'objectDetailWithheld'>,
): { text: string; withheld: boolean } | null {
  const d = fromFlag(
    value === '' ? null : value,
    task.objectDetailWithheld,
    '',
    WITHHELD_BECAUSE.taskObjectDetail,
  );
  if (d.state === 'shown') return { text: d.value!, withheld: false };
  if (d.state === 'withheld') return { text: d.placeholder, withheld: true };
  return null;
}
